/**
 * batch-54 klant-akkoord-mail (FO-412) - PURE helpers rond de klant-reply op de
 * goedkeurings-mail.
 *
 * Alles hier is I/O-vrij en daarmee los unit-testbaar: de referentiecode die op
 * de verzendmail gaat en er bij ontvangst weer af komt, het uitpellen van de
 * NIEUWE reply-tekst (zodat het gequote origineel + disclaimers nooit meegaan
 * naar de AI), de herkenning van automatische antwoorden, het normaliseren van
 * `In-Reply-To`/`References` en het versietoken waarmee we zien of het overzicht
 * ná verzending nog gewijzigd is.
 *
 * Bewust GEEN node:crypto: deze module wordt ook door de frontends geladen. Het
 * versietoken hoeft niet cryptografisch te zijn, alleen deterministisch en
 * botsingsarm over de handvol velden die het overzicht inhoudelijk bepalen.
 */

// ─── Auto-verwerk-drempel ────────────────────────────────────────────────────

/**
 * Minimale zekerheid (hele procenten) waarbij een AI-classificatie ZONDER mens
 * verwerkt mag worden. Alles daaronder gaat naar de review-wachtrij: kwaliteit
 * boven volledig automatisch (spec-aanname A1). Eén constante, zodat de drempel
 * op één plek bijgesteld kan worden.
 */
export const KLANT_AKKOORD_MAIL_ZEKERHEID_DREMPEL = 85

// ─── Referentiecode ──────────────────────────────────────────────────────────

/**
 * Bouwt de referentiecode voor het onderwerp van de goedkeurings-mail, bv.
 * `[REF: FO-2026-0123]`. De code is het conceptnummer van het facturatie-
 * overzicht; klanten laten hem bij een reply in het onderwerp staan (`RE: ...`),
 * waardoor hij de terugval-match is als de mail-headers niets opleveren.
 */
export function bouwAkkoordReferentieCode(conceptNummer: string): string {
  return `[REF: ${conceptNummer.trim()}]`
}

/**
 * STRIKTE regex (spec §11.2): hoofdletters/cijfers met koppeltekens, tussen
 * `[REF:` en `]`. Bewust geen losse woorden of vrije tekst - liever geen match
 * (en dus de niet-gekoppeld-lijst) dan een verkeerde koppeling.
 */
const REFERENTIE_CODE_RE = /\[\s*REF\s*:\s*([A-Z0-9]{2,}(?:-[A-Z0-9]{1,10}){0,4})\s*\]/i

/**
 * Haalt de referentiecode uit een onderwerp. Geeft de code in HOOFDLETTERS
 * terug (conceptnummers zijn hoofdletters; mailclients laten de casing intact,
 * maar sommige gateways niet). `null` als het onderwerp geen geldige code bevat.
 */
export function parseAkkoordReferentieCode(onderwerp: string | null | undefined): string | null {
  if (!onderwerp) return null
  const m = REFERENTIE_CODE_RE.exec(onderwerp)
  const code = m?.[1]?.trim()
  return code ? code.toUpperCase() : null
}

// ─── Message-id-normalisatie ─────────────────────────────────────────────────

/**
 * Splitst een `In-Reply-To`- of `References`-headerwaarde in losse message-ids
 * MET punthaken (`<...>`), zoals ze ook op `OutgoingEmail.internetMessageId`
 * staan. `References` bevat een keten; de LAATSTE is de directe voorganger, dus
 * we behouden de volgorde en de caller probeert van achter naar voren.
 * Ontdubbeld, lege waarden weggefilterd.
 */
export function parseMessageIds(header: string | null | undefined): string[] {
  if (!header) return []
  const gevonden = header.match(/<[^<>\s]+>/g) ?? []
  const uniek: string[] = []
  for (const id of gevonden) {
    if (!uniek.includes(id)) uniek.push(id)
  }
  return uniek
}

// ─── Automatische antwoorden (out-of-office) ─────────────────────────────────

/** Onderwerp-markers voor een afwezigheidsantwoord (NL/EN/DE/FR). */
const OOO_ONDERWERP_RE =
  /\b(out of (the )?office|automatic(al)? reply|automatisch(e)? antwoord|afwezigheid(sbericht)?|niet aanwezig|abwesenheit|automatische antwort|r[ée]ponse automatique|auto[- ]?reply|autoreply|vacation reply)\b/i

/**
 * Headers die een bericht als machinaal gegenereerd markeren. `Auto-Submitted`
 * (RFC 3834) is de gezaghebbende; de rest zijn de gangbare de-facto varianten
 * van Exchange/Outlook en oudere mailsystemen.
 */
const OOO_HEADER_NAMEN = new Set([
  'auto-submitted',
  'x-auto-response-suppress',
  'x-autoreply',
  'x-autorespond',
  'x-autoresponder',
  'x-office-auto-reply',
])

export interface AutoAntwoordInput {
  onderwerp: string | null | undefined
  /** Headernaam -> waarde (namen case-insensitief aangeleverd of niet). */
  headers?: Record<string, string> | null
}

/**
 * Is dit een automatisch antwoord (out-of-office / auto-reply)? Zulke mails
 * worden herkend en genegeerd - ze mogen nooit een besluit of review-item
 * opleveren (spec §6.6). Bounces/NDR's vallen onder het FO-146-vangnet en
 * worden apart herkend (zie `isBounceOnderwerp`).
 */
export function isAutomatischAntwoord(input: AutoAntwoordInput): boolean {
  const headers = input.headers ?? {}
  for (const [naam, waarde] of Object.entries(headers)) {
    const key = naam.trim().toLowerCase()
    if (!OOO_HEADER_NAMEN.has(key)) continue
    const v = (waarde ?? '').trim().toLowerCase()
    // `Auto-Submitted: no` betekent expliciet "dit is GEEN automaat".
    if (key === 'auto-submitted' && (v === '' || v === 'no')) continue
    return true
  }
  // `Precedence: auto_reply|bulk|junk` - alleen deze waarden, niet 'list'.
  const precedence = zoekHeader(headers, 'precedence')?.trim().toLowerCase() ?? ''
  if (precedence === 'auto_reply' || precedence === 'bulk' || precedence === 'junk') return true

  return OOO_ONDERWERP_RE.test(input.onderwerp ?? '')
}

/**
 * Onderwerp-heuristiek voor een bounce/NDR. Spiegelt de markers van de
 * bestaande FO-146-bounce-scan zodat beide kanten dezelfde mails herkennen; een
 * NDR op de akkoord-mailbox is nooit een klant-besluit.
 */
export function isBounceOnderwerp(input: {
  onderwerp: string | null | undefined
  afzenderAdres: string | null | undefined
}): boolean {
  const from = (input.afzenderAdres ?? '').toLowerCase()
  if (
    from.includes('postmaster@') ||
    from.includes('mailer-daemon') ||
    from.startsWith('microsoftexchange')
  ) {
    return true
  }
  return /undeliverable|onbestelbaar|delivery (status notification|has failed|failure)|mail delivery (failed|subsystem)|returned mail|failure notice|niet afgeleverd/i.test(
    input.onderwerp ?? '',
  )
}

/** Case-insensitieve header-lookup. */
function zoekHeader(headers: Record<string, string>, naam: string): string | null {
  const doel = naam.toLowerCase()
  for (const [k, v] of Object.entries(headers)) {
    if (k.trim().toLowerCase() === doel) return v
  }
  return null
}

// ─── Reply-extractie ─────────────────────────────────────────────────────────

/**
 * Regels die het begin van het GEQUOTE origineel markeren. Alles vanaf de
 * eerste treffer valt af; wat erboven staat is de nieuwe tekst van de klant.
 * Bewust ruim (NL + EN + de Outlook-scheidingslijn), want meesturen van het
 * origineel is erger dan een iets te korte reply: het origineel bevat onze
 * eigen "Antwoord met akkoord"-instructie en zou de classificatie vervuilen.
 */
const QUOTE_START_PATRONEN: RegExp[] = [
  /^-{2,}\s*(oorspronkelijk bericht|original message|origineel bericht)\s*-{2,}\s*$/i,
  /^_{10,}\s*$/,
  /^-{10,}\s*$/,
  /^\s*(van|from)\s*:\s*.+$/i,
  /^\s*(op|on)\b.{0,120}\b(schreef|wrote|geschreven door)\b.*:?\s*$/i,
  /^\s*(verzonden vanaf|sent from) (mijn|my) .{0,40}$/i,
  /^\s*>{1,}/,
  /^\s*(beantwoorden|reply|forward|doorsturen)\s*\|/i,
]

/**
 * Regels die een handtekening of disclaimer inluiden. Alles vanaf de treffer
 * valt af.
 */
const DISCLAIMER_PATRONEN: RegExp[] = [
  /^--\s*$/,
  /^__\s*$/,
  /^\s*(met vriendelijke groet|vriendelijke groet|met vriendelijke groeten|hartelijke groet|kind regards|best regards|mit freundlichen gr)/i,
  /^\s*(disclaimer|dit bericht (is|kan) vertrouwelijk|this (e-?mail|message) (is|and any)|de informatie verzonden met dit)/i,
  /^\s*(p\.?s\.? )?denk aan het milieu/i,
]

/** Maximale lengte van de tekst die naar de AI gaat (kosten + prompt-hygiëne). */
export const KLANT_AKKOORD_MAIL_MAX_TEKST = 4000

/**
 * Pelt de NIEUWE tekst uit een reply: alles boven het gequote origineel, zonder
 * handtekening/disclaimer. Normaliseert regeleindes en knipt herhaalde lege
 * regels weg. Geeft een lege string terug als er geen nieuwe tekst overblijft
 * (dan is het een lege reply -> review-item, spec §6.7).
 *
 * De disclaimer-knip wordt alleen toegepast als er tekst vóór de disclaimer
 * staat: een mail die MET "Met vriendelijke groet" begint zou anders volledig
 * verdampen.
 */
export function extraheerNieuweReplyTekst(body: string | null | undefined): string {
  const genormaliseerd = (body ?? '').replace(/\r\n?/g, '\n')
  if (genormaliseerd.trim() === '') return ''

  const regels = genormaliseerd.split('\n')

  const quoteIndex = eersteTreffer(regels, QUOTE_START_PATRONEN, 0)
  const zonderQuote = quoteIndex === -1 ? regels : regels.slice(0, quoteIndex)

  // Disclaimer alleen knippen als er iets vóór staat (index > eerste niet-lege).
  const eersteInhoud = zonderQuote.findIndex((r) => r.trim() !== '')
  const disclaimerIndex =
    eersteInhoud === -1 ? -1 : eersteTreffer(zonderQuote, DISCLAIMER_PATRONEN, eersteInhoud + 1)
  const kern = disclaimerIndex === -1 ? zonderQuote : zonderQuote.slice(0, disclaimerIndex)

  return kern
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
    .slice(0, KLANT_AKKOORD_MAIL_MAX_TEKST)
}

function eersteTreffer(regels: string[], patronen: RegExp[], vanaf: number): number {
  for (let i = Math.max(vanaf, 0); i < regels.length; i++) {
    const regel = regels[i] ?? ''
    if (patronen.some((p) => p.test(regel))) return i
  }
  return -1
}

/**
 * Zet een HTML-mailbody om naar platte tekst vóór de reply-extractie. Bewust
 * minimaal (geen HTML-parser-dependency): block-tags worden regeleindes, tags
 * verdwijnen, entiteiten worden ontdaan. Genoeg om de quote-markers en de
 * nieuwe tekst betrouwbaar te herkennen.
 */
export function htmlNaarTekst(html: string | null | undefined): string {
  if (!html) return ''
  return html
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<(script|style)[\s\S]*?<\/\1>/gi, '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|tr|li|h[1-6]|blockquote)>/gi, '\n')
    .replace(/<(hr)\s*\/?>/gi, '\n__________\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

// ─── Afzender-controle ───────────────────────────────────────────────────────

/**
 * Hoort dit afzenderadres bij de klant? Vergelijkt case-insensitief tegen de
 * bekende klant-adressen (factuuradres, algemeen adres, contactpersonen, cc).
 * Een reply van een ander adres wordt WEL verwerkt maar altijd als review-item
 * (spec §6.1) - een akkoord moet van de klant komen.
 */
export function isBekendeKlantAfzender(
  afzenderAdres: string | null | undefined,
  bekendeAdressen: Array<string | null | undefined>,
): boolean {
  const adres = (afzenderAdres ?? '').trim().toLowerCase()
  if (adres === '') return false
  return bekendeAdressen.some((a) => (a ?? '').trim().toLowerCase() === adres)
}

// ─── Versietoken ─────────────────────────────────────────────────────────────

/** De inhoudelijke essentie van een overzicht; wijzigt die, dan wijzigt het token. */
export interface OverzichtVersieInput {
  conceptNummer: string
  /** Bedragen als string (Decimal.toString()) zodat afronding nooit meespeelt. */
  subtotaal: string
  btw: string
  totaal: string
  /** Aantal regels + hun werkbonnummers, gesorteerd (volgorde-onafhankelijk). */
  werkbonnummers: string[]
}

/**
 * Deterministisch versietoken over de inhoud van een facturatie-overzicht. Bij
 * het versturen naar de klant leggen we het token vast in het audit-event; komt
 * de reply binnen, dan berekenen we het opnieuw. Wijkt het af, dan is het
 * overzicht ná verzending gewijzigd en gaat het mail-besluit naar review met
 * versie-waarschuwing (spec §6.2, zelfde principe als de offerte-besluiten).
 *
 * FNV-1a (32-bit) x2 met verschillende seeds -> 16 hex-tekens. Geen crypto
 * nodig: het token beschermt niet tegen manipulatie maar detecteert drift.
 */
export function bouwOverzichtVersieToken(input: OverzichtVersieInput): string {
  const basis = [
    input.conceptNummer.trim(),
    input.subtotaal.trim(),
    input.btw.trim(),
    input.totaal.trim(),
    [...input.werkbonnummers].map((w) => w.trim()).sort().join(','),
  ].join('|')
  return `${fnv1a(basis, 0x811c9dc5)}${fnv1a(basis, 0x01000193)}`
}

function fnv1a(tekst: string, seed: number): string {
  let hash = seed >>> 0
  for (let i = 0; i < tekst.length; i++) {
    hash ^= tekst.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193) >>> 0
  }
  return hash.toString(16).padStart(8, '0')
}
